// src/platform/backendBinding.js
//
// Cross-environment session-replay guard (F12a-N Issue A, task-n8). A
// capacitor device is enrolled + authed against ONE backend at a time (the
// URL `services/apiBaseUrl.js` resolves for the currently-installed build —
// e.g. a device enrolled against prod, then a staging build is installed
// over it). The persisted session (JWT + station config) belongs to the OLD
// backend and is meaningless against the new one: the JWT won't validate
// (different signing secret per environment) and the station config's
// tenant binding is for the wrong tenant database entirely. Left alone,
// boot "restores" that stale session, hits `/tenants/config` with the old
// JWT, gets a 401, and the device is stuck in a broken half-authed state
// instead of landing cleanly on EnrollmentScreen.
//
// Fix: compare the CURRENTLY resolved backend URL against the one we
// persisted on the previous boot. If they differ, this is a backend switch
// — wipe the operator session + station/tenant binding so the device
// re-enrolls cleanly. Primary call site is `src/index.js`, awaited BEFORE
// `root.render()` on capacitor — see that file's header comment for why
// (a race between this check and AuthContext's own boot restore that a
// post-mount effect ordering fix couldn't close). PosAppShell.js also
// still calls this on mount as a secondary, now-usually-redundant defense
// layer. A missing marker (no PREVIOUS boot recorded at all) is NOT
// treated as automatically safe — see the first-boot-hole note on
// `ensureBackendBinding` below, where a missing marker with session
// evidence still present resets exactly like a confirmed mismatch.
//
// KNOWN LIMITATION (F12a-N task-n8, field-confirmed, "marker poisoning"):
// this whole file's detection is keyed off comparing URLs. A device that
// was poisoned by an OLDER, buggier build of this same gate (one that
// persisted the marker for the CURRENT backend while leaving a stale
// session from a DIFFERENT backend in place — see git history, commit
// 5f46d94) now has a marker that matches the current URL by construction.
// `storedUrl === currentUrl` short-circuits both branches below to "no
// reset needed" — this file can never detect or repair that device again,
// no matter how many more boots happen, because from its point of view
// nothing has changed. This is not a bug in the comparison logic; it's a
// structural blind spot of marker-based detection once the marker itself
// is wrong. The actual repair for an already-poisoned device is
// context/AuthContext.js's marker-INDEPENDENT self-heal (Fix A): it reacts
// to the backend's own 401 response when replaying a stored token during
// boot restore, which stays correct regardless of what the marker says.
import host from '../host';
import { resolveApiUrl } from '../services/apiBaseUrl';
import { clearStationConfig, getStationConfig } from '../station/stationConfig';

export const AUTH_BACKEND_KEY = 'aqana.authBackend';

// Resolves the same API base URL api.js computes (same inputs, same pure
// resolver) — never duplicate the resolution logic itself.
export function resolveCurrentBackendUrl() {
    return resolveApiUrl({
        hostType: host.type,
        backendEnv: localStorage.getItem('aqana.backendEnv'),
        envVar: process.env.REACT_APP_API_URL,
        hostname: window.location.hostname,
    });
}

// The exact operator-session keys AuthContext.logout()'s capacitor-station
// branch clears (context/AuthContext.js ~L491-515: authService.logout()'s
// keys — services/api.js ~L211-221 — plus the branch's own additional
// removes). Kept as a literal list here (rather than importing
// services/api, which would pull axios into this pre-provider boot gate)
// — if that key set changes, update both places.
const AUTH_SESSION_KEYS = [
    // authService.logout() (services/api.js)
    'token',
    'access_token',
    'user_role',
    'username',
    'employee_name',
    'employee_id',
    'auth_type',
    'isAuthenticated',
    'authType',
    'role_name',
    // AuthContext.logout()'s isCapacitorStation branch additions
    'permissions',
    'credentialType',
    'cached_translations',
    'token_expires_at',
    'employeeId',
];

function clearAuthSession() {
    AUTH_SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
}

// Shared reset primitive — the operator session + station/tenant binding
// wipe used both by ensureBackendBinding's own mismatch/no-evidence
// branches below AND by AuthContext's marker-independent boot self-heal
// (F12a-N task-n8 Fix A, context/AuthContext.js's fetchTenantConfig
// isBootRestore branch). Exported so that second call site never
// duplicates this sequence.
export async function resetDeviceSession() {
    clearAuthSession();
    await clearStationConfig();
}

// Capacitor-gated. Call once, on boot, before StationProvider/AuthContext
// do anything. No-op on web/Electron (those builds don't switch backend
// per-install the way a capacitor binary does).
//
// Persistence choice: `aqana.authBackend` is written HERE at the end of
// every boot check (both the "matched" and "first boot" cases), not at
// EnrollmentScreen login success. Simpler — one write point, and it's
// already the value this function needs to compare against next boot — see
// plan note re: "writing it in the same backendBinding check on next boot
// is acceptable if simpler".
//
// First-boot hole (F12a-N task-n8 Bug A, field-confirmed): "no stored
// marker" was originally treated as "genuinely new device, nothing to
// reset" unconditionally. That's wrong — the marker can be missing while a
// real session ISN'T (an app upgrade installed over an existing enrollment
// that predates this gate, or the single `aqana.authBackend` key alone got
// dropped independently of everything else). A device carrying a prod JWT
// + prod station config that boots straight into a staging build under the
// old logic "persisted staging as bound" and left the stale prod session in
// place — replayed against staging, 401 on /tenants/config, half-authed
// state. So on a missing marker we don't assume anything: we check for
// EVIDENCE of a prior session (any auth-session key in localStorage, or a
// persisted station config — the same two things a real enrollment leaves
// behind) and if either is present, provenance is unknown and we reset
// exactly like a confirmed cross-backend mismatch. Only a device with
// NEITHER — the actual fresh-out-of-the-box case — gets the cheap
// persist-only path.
export async function ensureBackendBinding() {
    if (host.type !== 'capacitor') return;

    const currentUrl = resolveCurrentBackendUrl();
    const storedUrl = localStorage.getItem(AUTH_BACKEND_KEY);

    if (storedUrl) {
        if (storedUrl !== currentUrl) {
            await resetDeviceSession();
        }
    } else {
        const hasSessionEvidence = AUTH_SESSION_KEYS.some((key) => localStorage.getItem(key) != null)
            || (await getStationConfig()) != null;
        if (hasSessionEvidence) {
            await resetDeviceSession();
        }
    }

    localStorage.setItem(AUTH_BACKEND_KEY, currentUrl);
}
